import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useReactTable, getCoreRowModel, getPaginationRowModel, getSortedRowModel } from "@tanstack/react-table";
import { Table, Paper, FormControlLabel, Switch, Menu, MenuItem, IconButton, Typography, InputAdornment, TextField, Box, Button } from "@mui/material";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import PushPinIcon from "@mui/icons-material/PushPin";
import { RestartAlt } from "@mui/icons-material";
import { grey } from "@mui/material/colors";
import TanstackFooterPagination from "./TanstackFooterPagination";
import { TableLoadingOverlay } from "./data";
import TanstackDatagridHead from "./TanstackDatagridHead/TanstackDatagridHead";
import TanstackDatagridBody from "./TanstackDatagridBody/TanstackDatagridBody";

const defColumnMinWidth = 90;
const defColumnMaxWidth = Number.MAX_SAFE_INTEGER;
const defCellpadding = 1;

const getNumericColumnSize = (size, fallback) => {
	const parsedSize = Number(size);
	return Number.isFinite(parsedSize) ? parsedSize : fallback;
};

export const buildRenderContext = ({ value, row, rowModel, column, cell, header, table }) => ({
	value, row, rowModel, column, cell, header, table,
});

const resolveAdornment = (adornment, params) => {
	if (!adornment) return null;
	return typeof adornment === "function" ? adornment(params) : adornment;
};

const removeDuplicatesByRowId = (rows, getRowId) => {
	const seen = new Set();
	return rows.filter((row) => {
		const id = getRowId(row);
		if (seen.has(id)) return false;
		seen.add(id);
		return true;
	});
};

export const DefaultRenderCell = ({ value, column }) => {
	const start = resolveAdornment(column?.startAdornment, { value });
	const end = resolveAdornment(column?.endAdornment, { value });
	return (
		<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", gap: 0.5, padding: defCellpadding, boxSizing: "border-box", whiteSpace: "nowrap", overflow: "hidden" }}>
			{start && <Box sx={{ opacity: 0.7 }}>{start}</Box>}
			<Typography sx={{ flexGrow: 1, overflow: "hidden", textOverflow: "ellipsis", textAlign: column.align === "center" ? "center" : column.align === "right" ? "flex-end" : "flex-start" }}>
				{value ?? ""}
			</Typography>
			{end && <Box sx={{ opacity: 0.7 }}>{end}</Box>}
		</Box>
	);
};

export const DefaultRenderHeader = ({ column, headerText, children }) => (
	<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: defCellpadding, boxSizing: "border-box" }}>
		<Typography variant="subtitle1" fontWeight={600} flex={1} color={grey[900]} noWrap
			textAlign={column.meta?.headerAlign === "center" ? "center" : column.meta?.headerAlign === "right" ? "flex-end" : "flex-start"}>
			{headerText}
		</Typography>
		{children}
	</Box>
);

export const DefaultRenderEditCell = React.forwardRef(({ value, type = "text", column, onBlur, onKeyDown }, ref) => {
	const startAdornment = resolveAdornment(column?.startAdornment, { value });
	const endAdornment = resolveAdornment(column?.endAdornment, { value });
	return (
		<TextField
			inputRef={ref}
			defaultValue={value}
			type={type}
			variant="standard"
			autoFocus
			fullWidth
			onBlur={onBlur}
			onKeyDown={onKeyDown}
			InputProps={{
				disableUnderline: true,
				startAdornment: startAdornment ? <InputAdornment position="start" sx={{ position: "absolute", left: 8, height: "100%", maxHeight: "none" }}>{startAdornment}</InputAdornment> : null,
				endAdornment: endAdornment ? <InputAdornment position="end" sx={{ position: "absolute", right: 8, height: "100%", maxHeight: "none" }}>{endAdornment}</InputAdornment> : null,
			}}
			sx={{
				width: "100%", height: "100%",
				"& .MuiInputBase-root": { height: "100%", padding: 0, alignItems: "center", pr: endAdornment ? 4 : 1, pl: startAdornment ? 4 : 1 },
				"& input": { height: "100%", padding: "0 6px", boxSizing: "border-box", textAlign: column?.dataType === "DECIMAL" ? "right" : "left" },
			}}
		/>
	);
});

const TanstackDatagrid = ({
	columns = [],
	headerHeight = 50,
	headerRowStyle = { background: grey[200] },
	rowHeight = 45,
	pageData: pageDataProp,
	setPageData: setPageDataProp,
	rowCount: rowCountProp,
	setRowCount: setRowCountProp,
	filterObj: filterObjProp,
	setFilterObj: setFilterObjProp,
	onPageChange = () => {},
	onSortChange = () => {},
	onColumnFilterChange = () => {},
	validateCell = null,
	isLoading = false,
	emptyMessage = "No records found",
	processRowUpdate = (newRow, oldRow, column) => {},
	handleRowClick = ({ row }, e) => {},
	isCellEditable = ({ row, column }) => column.editable || false,
	checkboxSelection = false,
	selectedRowIds: selectedRowIdsProp,
	setSelectedRowIds: setSelectedRowIdsProp,
	processCheckboxSelectionChange = (selectedIds) => {},
	getRowId = (row) => row.id,
	disablePagination = false,
	rowsPerPage = 50,
	serverSidePagination = true,
	disableCache = false,
	getServerSideData = (limit, offset, filterObj) => ({ rows: [], total_rows_count: 0 }),
	enableHorizontalScroll = false,
	allowColumnResize = true,
	children: headerJSX,
}) => {
	const isPaginationEnabled = !disablePagination;

	const [columnVisibility, setColumnVisibility] = useState({});
	const [columnPinning, setColumnPinning] = useState({ left: [], right: [] });
	const [columnSizing, setColumnSizing] = useState({});
	const [editingCell, setEditingCell] = useState(null);
	const [validationErrors, setValidationErrors] = useState({});
	const [rowSelection, setRowSelection] = useState({});
	const [selectedRowIdsState, setSelectedRowIdsState] = useState([]);
	const [pageDataState, setPageDataState] = useState([]);
	const [rowCountState, setRowCountState] = useState(0);
	const [columnFiltersState, setColumnFiltersState] = useState({});
	const [currentPageState, setCurrentPageState] = useState(0);
	const [sorting, setSorting] = useState([]);
	const [offset, setOffset] = useState(0);
	const [loading, setLoading] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [containerWidth, setContainerWidth] = useState(0);
	const userResizedCols = useRef({});

	const containerRef = useRef(null);
	const inputRef = useRef(null);
	const abortRef = useRef(null);
	const debounceRef = useRef(null);
	const cacheRef = useRef(new Map());

	const localData = typeof setPageDataProp === "function" ? pageDataProp : pageDataState;
	const setLocalData = typeof setPageDataProp === "function" ? setPageDataProp : setPageDataState;
	const serverRowCount = typeof setRowCountProp === "function" ? rowCountProp : rowCountState;
	const setServerRowCount = typeof setRowCountProp === "function" ? setRowCountProp : setRowCountState;
	const columnFilters = typeof setFilterObjProp === "function" ? filterObjProp : columnFiltersState;
	const setColumnFilters = typeof setFilterObjProp === "function" ? setFilterObjProp : setColumnFiltersState;
	const selectedRowIds = typeof setSelectedRowIdsProp === "function" ? selectedRowIdsProp || [] : selectedRowIdsState;
	const setSelectedRowIds = typeof setSelectedRowIdsProp === "function" ? setSelectedRowIdsProp : setSelectedRowIdsState;

	const safeColumns = useMemo(
		() =>
			Array.isArray(columns)
				? columns.map((col) => ({
						key: col?.key,
						name: col?.name || col.key.split("_").join(" ").toUpperCase(),
						editable: Boolean(col?.editable),
						renderCell: col?.renderCell || DefaultRenderCell,
						renderHeader: col?.renderHeader || DefaultRenderHeader,
						headerAlign: col?.headerAlign || "left",
						renderEditCell: col?.editable ? col?.renderEditCell || DefaultRenderEditCell : null,
						constraints: col?.constraints || {},
						flex: col?.flex ?? (col?.maxWidth != null ? 0 : 1),
						minWidth: getNumericColumnSize(col?.minWidth, defColumnMinWidth),
						maxWidth: getNumericColumnSize(col?.maxWidth, defColumnMaxWidth),
						width: col?.width != null ? getNumericColumnSize(col.width, undefined) : undefined,
						dataType: col?.data_type,
						startAdornment: col?.startAdornment ?? null,
						endAdornment: col?.endAdornment ?? null,
						align: col?.align || "left",
					}))
				: [],
		[columns],
	);

	const tanstackColumns = useMemo(
		() =>
			safeColumns.map((col) => ({
				accessorKey: col.key,
				header: col.name,
				size: col.width || col.minWidth || defColumnMinWidth,
				minSize: col.minWidth,
				maxSize: !allowColumnResize ? col?.maxWidth || col.width || col.minWidth : defColumnMaxWidth,
				cell: (info) =>
					col.renderCell
						? col.renderCell(buildRenderContext({ value: info.getValue(), row: info.row.original, rowModel: info.row, column: info.column.columnDef.meta, cell: info.cell, table: info.table }))
						: info.getValue(),
				meta: {
					key: col.key, editable: col.editable, renderEditCell: col.renderEditCell,
					renderHeader: col.renderHeader, headerAlign: col.headerAlign, constraints: col.constraints,
					flex: col.flex, minWidth: col.minWidth, maxWidth: col.maxWidth, width: col.width,
					dataType: col.dataType, startAdornment: col.startAdornment, endAdornment: col.endAdornment, align: col.align,
				},
			})),
		[safeColumns],
	);

	const table = useReactTable({
		data: localData,
		columns: tanstackColumns,
		pageCount: isPaginationEnabled ? Math.ceil(serverRowCount / rowsPerPage) : undefined,
		manualPagination: isPaginationEnabled,
		manualSorting: true,
		manualFiltering: true,
		enableRowSelection: checkboxSelection,
		enableColumnResizing: allowColumnResize,
		columnResizeMode: "onEnd",
		columnResizeDirection: "ltr",
		state: {
			pagination: isPaginationEnabled ? { pageIndex: currentPageState, pageSize: rowsPerPage } : undefined,
			columnVisibility, columnPinning, columnSizing, sorting, rowSelection,
		},
		getRowId: (row) => getRowId(row),
		onRowSelectionChange: (updater) => {
			const nextSelection = typeof updater === "function" ? updater(rowSelection) : updater;
			setRowSelection(nextSelection);
			const nextSelectedIds = Object.keys(nextSelection).filter((id) => nextSelection[id]);
			setSelectedRowIds(nextSelectedIds);
			processCheckboxSelectionChange(nextSelectedIds);
		},
		onColumnVisibilityChange: setColumnVisibility,
		onColumnPinningChange: setColumnPinning,
		onColumnSizingChange: (updater) => {
			setColumnSizing((prev) => {
				const next = typeof updater === "function" ? updater(prev) : updater;
				// track which columns user manually resized
				Object.keys(next).forEach((key) => {
					if (next[key] !== prev[key]) userResizedCols.current[key] = next[key];
				});
				return next;
			});
		},
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: isPaginationEnabled ? getPaginationRowModel() : undefined,
		getSortedRowModel: getSortedRowModel(),
		onSortingChange: (updater) => {
			const newSort = typeof updater === "function" ? updater(sorting) : updater;
			setSorting(newSort);
			onSortChange(newSort);
			if (!serverSidePagination) return;
			cacheRef.current.clear();
			setOffset(0);
			if (isPaginationEnabled) setCurrentPageState(0);
			else setHasMore(true);
		},
	});

	// Sync checkbox selection
	useEffect(() => {
		if (!checkboxSelection) return;
		const nextRowSelection = selectedRowIds.reduce((acc, id) => { acc[id] = true; return acc; }, {});
		setRowSelection(nextRowSelection);
	}, [checkboxSelection, selectedRowIds]);

	// ResizeObserver — track container width
	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const ro = new ResizeObserver((entries) => {
			const width = entries[0]?.contentRect?.width;
			if (width) setContainerWidth(width);
		});
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	// Flex-aware column sizing
	useEffect(() => {
		if (!containerWidth) return;
		if (table.getState().columnSizingInfo.isResizingColumn) return;
		const checkboxW = checkboxSelection ? 48 : 0;
		const totalMinWidth = safeColumns.reduce((acc, c) => acc + c.minWidth, 0) + checkboxW;
		const availableWidth = containerWidth - checkboxW;
		const flexCols = safeColumns.filter((c) => c.flex > 0);
		const nonFlexWidth = safeColumns.filter((c) => c.flex === 0).reduce((acc, c) => acc + c.minWidth, 0);
		const sizing = {};

		if (containerWidth >= totalMinWidth && flexCols.length > 0) {
			const flexMinWidth = flexCols.reduce((acc, c) => acc + c.minWidth, 0);
			const extraSpace = availableWidth - nonFlexWidth - flexMinWidth;
			const totalFlex = flexCols.reduce((acc, c) => acc + c.flex, 0);
			safeColumns.forEach((c) => {
				// if user manually resized this column, keep their size
				if (allowColumnResize && userResizedCols.current[c.key] != null) {
					sizing[c.key] = userResizedCols.current[c.key];
				} else if (c.flex > 0) {
					sizing[c.key] = Math.max(c.minWidth, Math.floor(c.minWidth + (c.flex / totalFlex) * extraSpace));
				} else {
					sizing[c.key] = c.minWidth;
				}
			});
		} else {
			safeColumns.forEach((c) => {
				if (allowColumnResize && userResizedCols.current[c.key] != null) {
					sizing[c.key] = userResizedCols.current[c.key];
				} else {
					sizing[c.key] = c.minWidth;
				}
			});
		}
		setColumnSizing(sizing);
	}, [containerWidth, safeColumns, checkboxSelection]);

	const totalTableSize = table.getTotalSize() + (checkboxSelection ? 48 : 0);
	const tableWidth = allowColumnResize ? `${totalTableSize}px` : `${Math.max(totalTableSize, containerWidth)}px`;

	const overlayColumnWidths = useMemo(() => {
		const widths = table.getVisibleLeafColumns().map((col) => col.getSize());
		return checkboxSelection ? [48, ...widths] : widths;
	}, [table, checkboxSelection, columnVisibility, columnSizing]);

	const overlayTableWidth = useMemo(() => {
		return `${table.getTotalSize() + (checkboxSelection ? 48 : 0)}px`;
	}, [table, checkboxSelection, columnVisibility, columnSizing]);

	const visibleSelectedCount = useMemo(() => {
		if (!checkboxSelection) return 0;
		return table.getRowModel().rows.filter((row) => selectedRowIds.includes(getRowId(row.original))).length;
	}, [checkboxSelection, table, selectedRowIds, localData]);

	const clearSelection = () => {
		setRowSelection({});
		setSelectedRowIds([]);
		processCheckboxSelectionChange([]);
	};

	const handlePageChangeInternal = (nextPage) => {
		setCurrentPageState(nextPage);
		setOffset(nextPage * rowsPerPage);
		if (typeof onPageChange === "function") onPageChange(nextPage);
	};

	useEffect(() => {
		if (editingCell && inputRef.current) inputRef.current.focus();
	}, [editingCell]);

	const handleCellBlur = (row, columnId, value, constraints) => {
		const rowId = getRowId(row);
		const error = validateCell ? validateCell(row, columnId, value, constraints) : null;
		setValidationErrors((prev) => ({ ...prev, [`${rowId}_${columnId}`]: error || null }));
		if (error) return;
		const updatedRow = { ...row, [columnId]: value };
		const finalRow = processRowUpdate(updatedRow, row, columnId) || updatedRow;
		setLocalData((prev) => prev.map((r) => (getRowId(r) === rowId ? finalRow : r)));
		setEditingCell(null);
	};

	const handleKeyDown = (e, rowIndex, columnIndex, row, columnId, constraints) => {
		const id = getRowId(row);
		if (e.key === "Enter") {
			handleCellBlur(row, columnId, e.target.value, constraints);
			const nextRow = table.getRowModel().rows[rowIndex + 1];
			if (nextRow) setEditingCell({ rowId: getRowId(nextRow.original), columnId });
		}
		if (e.key === "Tab") {
			e.preventDefault();
			const cells = table.getRowModel().rows[rowIndex]?.getVisibleCells();
			const nextCell = e.shiftKey ? cells[columnIndex - 1] : cells[columnIndex + 1];
			if (nextCell) setEditingCell({ rowId: id, columnId: nextCell.column.id });
		}
	};

	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);

	const resetServerDataState = ({ resetPage = false } = {}) => {
		cacheRef.current.clear();
		setOffset(0);
		if (resetPage && isPaginationEnabled) setCurrentPageState(0);
		if (!isPaginationEnabled) setHasMore(true);
	};

	const togglePin = (colId, side) => {
		setColumnPinning((prev) => ({
			left: side === "left" ? (prev.left.includes(colId) ? prev.left.filter((c) => c !== colId) : [...prev.left, colId]) : prev.left.filter((c) => c !== colId),
			right: side === "right" ? (prev.right.includes(colId) ? prev.right.filter((c) => c !== colId) : [...prev.right, colId]) : prev.right.filter((c) => c !== colId),
		}));
	};

	const fetchServerData = ({ reset = false } = {}) => {
		if (!serverSidePagination) return;
		if (abortRef.current) abortRef.current.abort();
		const controller = new AbortController();
		abortRef.current = controller;
		const currentOffset = reset ? 0 : offset;
		const cacheKey = JSON.stringify({ limit: rowsPerPage, offset: currentOffset, filters: columnFilters, sort: sorting });
		if (!disableCache && cacheRef.current.has(cacheKey)) {
			const cached = cacheRef.current.get(cacheKey);
			setLocalData((prev) => (isPaginationEnabled ? cached.rows : reset ? cached.rows : [...prev, ...cached.rows]));
			setHasMore(cached.hasMore);
			return;
		}
		clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(async () => {
			setLoading(true);
			try {
				const res = await getServerSideData(rowsPerPage, currentOffset, columnFilters, controller.signal);
				const rows = removeDuplicatesByRowId(res?.rows || [], getRowId);
				const total = res?.total_rows_count ?? 0;
				setServerRowCount(total);
				const nextOffset = currentOffset + rows.length;
				const more = nextOffset < total;
				cacheRef.current.set(cacheKey, { rows, hasMore: more });
				setLocalData((prev) => (isPaginationEnabled ? rows : reset ? rows : [...prev, ...rows]));
				setOffset(nextOffset);
				setHasMore(more);
			} catch (e) {
				if (e.name !== "AbortError") console.error(e);
			} finally {
				setLoading(false);
			}
		}, 300);
	};

	useEffect(() => {
		if (isPaginationEnabled || !serverSidePagination) return;
		const el = containerRef.current;
		if (!el) return;
		const onScroll = () => {
			if (loading || !hasMore) return;
			if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) fetchServerData();
		};
		el.addEventListener("scroll", onScroll);
		return () => el.removeEventListener("scroll", onScroll);
	}, [isPaginationEnabled, serverSidePagination, loading, hasMore]);

	useEffect(() => {
		if (isPaginationEnabled || !serverSidePagination) return;
		resetServerDataState();
		fetchServerData({ reset: true });
	}, [isPaginationEnabled, serverSidePagination, rowsPerPage]);

	useEffect(() => {
		if (!serverSidePagination) return;
		clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			onColumnFilterChange(columnFilters);
			resetServerDataState({ resetPage: true });
			fetchServerData({ reset: true });
		}, 400);
		return () => clearTimeout(debounceRef.current);
	}, [columnFilters]);

	useEffect(() => {
		if (!isPaginationEnabled || !serverSidePagination) return;
		const nextOffset = currentPageState * rowsPerPage;
		setOffset(nextOffset);
		fetchServerData({ reset: false });
	}, [currentPageState, rowsPerPage]);

	return (
		<Paper sx={{ pl: 2, pr: 2, pb: 1, pt: 1, width: "100%", height: "100%", minHeight: 0, display: "flex", flexDirection: "column", boxSizing: "border-box" }}>
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
				<Box flexGrow={1} boxSizing="border-box">{headerJSX}</Box>
				<Box display="flex" columnGap={1} pl={2} alignItems="center">
					{checkboxSelection && selectedRowIds.length > 0 && (
						<>
							<Typography variant="body2" color="text.secondary">{selectedRowIds.length} selected, {visibleSelectedCount} visible</Typography>
							<Button size="small" onClick={clearSelection} startIcon={<RestartAlt />} variant="contained">Selection</Button>
						</>
					)}
					<IconButton onClick={(e) => setAnchorEl(e.currentTarget)}><ViewColumnIcon /></IconButton>
				</Box>
			</Box>

			<Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
				{table.getAllLeafColumns().map((col) => (
					<MenuItem key={col.id}>
						<FormControlLabel control={<Switch checked={col.getIsVisible()} onChange={col.getToggleVisibilityHandler()} />} label={col.columnDef.header} />
						<IconButton size="small" onClick={() => togglePin(col.id, "left")}><PushPinIcon fontSize="inherit" /></IconButton>
					</MenuItem>
				))}
			</Menu>

			<Box ref={containerRef} sx={{ position: "relative", flex: 1, overflow: "auto", minHeight: 0, boxSizing: "border-box" }}>
				<Table sx={{ tableLayout: "fixed", width: tableWidth, minWidth: `${table.getTotalSize()}px` }}>
					<TanstackDatagridHead
						table={table}
						headerHeight={headerHeight}
						headerRowStyle={headerRowStyle}
						checkboxSelection={checkboxSelection}
						allowColumnResize={allowColumnResize}
						columnFilters={columnFilters}
						setColumnFilters={setColumnFilters}
						buildRenderContext={buildRenderContext}
					/>
					{loading && isPaginationEnabled ? null : (
						<TanstackDatagridBody
							table={table}
							rowHeight={rowHeight}
							checkboxSelection={checkboxSelection}
							editingCell={editingCell}
							setEditingCell={setEditingCell}
							validationErrors={validationErrors}
							isCellEditable={isCellEditable}
							handleCellBlur={handleCellBlur}
							handleKeyDown={handleKeyDown}
							handleRowClick={handleRowClick}
							getRowId={getRowId}
							inputRef={inputRef}
							buildRenderContext={buildRenderContext}
						/>
					)}
				</Table>

				{((loading && localData.length > 0) || (loading && isPaginationEnabled)) && (
					<TableLoadingOverlay columnWidths={overlayColumnWidths} tableWidth={overlayTableWidth} rowHeight={rowHeight} rowCount={rowsPerPage} />
				)}
				{!isLoading && localData.length === 0 && (
					<Typography align="center" color="text.secondary" sx={{ mt: 3 }}>{emptyMessage}</Typography>
				)}
				{loading && localData.length > 0 && (
					<Box sx={{ py: 1, textAlign: "center", backgroundColor: grey[50] }}>
						<Typography variant="body1" color="text.secondary">Loading…</Typography>
					</Box>
				)}
			</Box>

			{isPaginationEnabled && (
				<TanstackFooterPagination count={serverRowCount} page={currentPageState} rowsPerPage={rowsPerPage} onPageChange={handlePageChangeInternal} />
			)}
		</Paper>
	);
};

export default TanstackDatagrid;
